import { useMemo, useState, type FormEvent } from "react";
import { AssetRefList, collectAssetRefs } from "../assets/AssetRef";
import { stripRefLines } from "../assets/refs";
import { parseAssetRef } from "../rooms/model";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dialog, EmptyState, Field, IconButton, Input, Segmented, Select, TextArea, useLayout, useToast } from "@agentsaas/ui";
import type { Actor, Agent, AgentId, Mail, MailPriority } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { useSession } from "../app/session";
import { formatDateTime, formatRelative } from "../app/format";
import { useAgents } from "../data/queries";
import { ck, useMailbox } from "../data/collab";
import { Loading } from "../layout/Loading";
import { Markdown } from "../chat/Markdown";
import { AgentStatusDot } from "../agent/TeamList";
import { CollabFrame } from "../project/ProjectChrome";
import { WorkSwitch } from "../project/WorkSwitch";
import { buildThreads, type MailThread } from "./threads";

type Box = "all" | "inbox" | "sent";
const BOXES: Box[] = ["all", "inbox", "sent"];
const PRIORITIES: MailPriority[] = ["low", "normal", "high", "urgent"];

/** /p/:pid/mail?agent=&thread=：agent 邮箱（按线程只读）+ 人给 agent 发邮件 */
export function MailPage() {
  const { pid = "" } = useParams();
  return (
    <CollabFrame pid={pid} section="mail">
      <MailView pid={pid} />
    </CollabFrame>
  );
}

function useQueryParam(name: string) {
  const [sp, setSp] = useSearchParams();
  const set = (v: string | null, push = false) =>
    setSp(
      (cur) => {
        const n = new URLSearchParams(cur);
        if (v) n.set(name, v);
        else n.delete(name);
        if (name === "agent") n.delete("thread");
        return n;
      },
      { replace: !push },
    );
  return [sp.get(name), set] as const;
}

function MailView({ pid }: { pid: string }) {
  const { t } = useT();
  const mobile = useLayout() === "mobile";
  const navigate = useNavigate();
  const agents = useAgents(pid);
  const list = agents.data ?? [];
  const [agentParam, setAgent] = useQueryParam("agent");
  const [threadId, setThread] = useQueryParam("thread");
  const [box, setBox] = useState<Box>("all");
  const [composing, setComposing] = useState(false);
  const aid = list.find((a) => a.id === agentParam)?.id ?? list[0]?.id;
  const inbox = useMailbox(aid, "inbox");
  const sent = useMailbox(aid, "sent");
  const threads = useMemo(() => (aid ? buildThreads(aid, inbox.data ?? [], sent.data ?? []) : []), [aid, inbox.data, sent.data]);
  const shown = threads.filter((x) => box === "all" || (box === "inbox" ? x.hasInbox : x.hasSent));
  const open = threads.find((x) => x.id === threadId);
  const nameOf = (a: Actor) => (a.type === "agent" ? (list.find((x) => x.id === a.id)?.name ?? a.id) : a.type === "user" ? t("mail.human") : t("task.by_system"));

  if (agents.isPending) return <Loading />;
  if (!list.length) return <EmptyState icon="mail" title={t("agent.empty")} />;

  const compose = (
    <Button variant="primary" icon="send" onClick={() => setComposing(true)} data-testid="mail-compose">
      {t("mail.compose")}
    </Button>
  );
  const dialog = <ComposeDialog pid={pid} open={composing} onClose={() => setComposing(false)} agents={list} defaultTo={aid} />;
  const threadList = (
    <ThreadList
      threads={shown}
      loading={inbox.isPending || sent.isPending}
      active={threadId}
      onOpen={(id) => setThread(id, mobile)}
      nameOf={nameOf}
      box={box}
      setBox={setBox}
    />
  );

  if (mobile) {
    if (open)
      return (
        <div className="mail mail--mobile">
          <div className="drill-head">
            <IconButton icon="back" label={t("common.back")} onClick={() => navigate(-1)} data-testid="mail-back" />
            <strong className="ellipsis">{open.subject}</strong>
          </div>
          <ThreadView pid={pid} thread={open} nameOf={nameOf} agents={list} />
        </div>
      );
    return (
      <div className="mail mail--mobile">
        <WorkSwitch pid={pid} active="mail" />
        <div className="mail__mhead">
          <Select aria-label={t("mail.agent")} value={aid} onChange={(e) => setAgent(e.target.value)} data-testid="mail-agent">
            {list.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
          {compose}
        </div>
        {threadList}
        {dialog}
      </div>
    );
  }

  return (
    <div className="mail">
      <nav className="mail__agents" aria-label={t("mail.agent")}>
        <div className="pane-head">
          <span className="grow">{t("mail.mailboxes")}</span>
        </div>
        <ul className="list">
          {list.map((a) => (
            <li key={a.id}>
              <button type="button" className={`list-item${a.id === aid ? " is-active" : ""}`} aria-current={a.id === aid ? "true" : undefined} onClick={() => setAgent(a.id)} data-testid="mail-agent-item">
                <AgentStatusDot status={a.status} />
                <span className="grow ellipsis">{a.name}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="mail__compose">{compose}</div>
      </nav>
      <section className="mail__threads" aria-label={t("mail.threads")}>
        {threadList}
      </section>
      <section className="mail__view" aria-label={t("mail.thread")}>
        {open ? <ThreadView pid={pid} thread={open} nameOf={nameOf} agents={list} /> : <EmptyState icon="mail" title={t("mail.pick_thread")} />}
      </section>
      {dialog}
    </div>
  );
}

function ThreadList({
  threads,
  loading,
  active,
  onOpen,
  nameOf,
  box,
  setBox,
}: {
  threads: MailThread[];
  loading: boolean;
  active: string | null;
  onOpen(id: string): void;
  nameOf(a: Actor): string;
  box: Box;
  setBox(b: Box): void;
}) {
  const { t, locale } = useT();
  return (
    <div className="mail__list">
      <div className="mail__filter">
        <Segmented<Box> label={t("mail.box")} value={box} onChange={setBox} options={BOXES.map((b) => ({ value: b, label: t(`mail.box.${b}`) }))} />
      </div>
      {loading ? (
        <Loading />
      ) : threads.length ? (
        <ul className="list" data-testid="mail-threads">
          {threads.map((th) => (
            <li key={th.id}>
              <button type="button" className={`list-item mail-thread${th.id === active ? " is-active" : ""}`} onClick={() => onOpen(th.id)} data-testid="mail-thread">
                <span className="grow mail-thread__main">
                  <span className="row mail-thread__top">
                    <strong className={`grow ellipsis${th.unread ? "" : " mail-thread__read"}`}>{th.subject}</strong>
                    <time className="muted small" dateTime={th.last.createdAt}>
                      {formatRelative(th.last.createdAt, locale)}
                    </time>
                  </span>
                  <span className="small muted ellipsis">
                    {[...new Set(th.mails.map((m) => nameOf(m.from)))].join(", ")}
                    {th.mails.length > 1 ? <Badge className="mail-thread__count">{th.mails.length}</Badge> : null}
                  </span>
                  <span className="small ellipsis mail-thread__snippet">{th.last.body.replace(/\s+/g, " ").slice(0, 120)}</span>
                </span>
                {th.unread ? <span className="mail-thread__dot" role="img" aria-label={t("mail.unread", { count: th.unread })} /> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="mail" title={t("mail.empty")} />
      )}
    </div>
  );
}

function ThreadView({ pid, thread, nameOf, agents }: { pid: string; thread: MailThread; nameOf(a: Actor): string; agents: Agent[] }) {
  const { t, locale } = useT();
  const tz = useSession().state.user?.timezone;
  const name = (aid: AgentId) => agents.find((a) => a.id === aid)?.name ?? aid;
  return (
    <article className="mail-thread-view" data-testid="mail-thread-view">
      <h2 className="page__title mail-thread-view__subject">{thread.subject}</h2>
      <ol className="mail-msgs">
        {thread.mails.map((m: Mail) => (
          <li key={m.id} className="card mail-msg" data-testid="mail-msg">
            <header className="mail-msg__head">
              <strong>{nameOf(m.from)}</strong>
              <span className="muted small">{t("mail.to", { names: m.to.map(name).join(", ") })}</span>
              <span className="grow" />
              {m.priority !== "normal" ? <Badge tone={m.priority === "low" ? "neutral" : "warning"}>{t(`mail.priority.${m.priority}`)}</Badge> : null}
              <time className="muted small" dateTime={m.createdAt}>
                {formatDateTime(m.createdAt, locale, tz)}
              </time>
            </header>
            <div className="small muted">{m.subject}</div>
            <Markdown text={stripRefLines(m.body)} />
            {m.attachments.some((r) => !parseAssetRef(r)) ? (
              <ul className="refs">
                {m.attachments
                  .filter((r) => !parseAssetRef(r))
                  .map((r) => (
                    <li key={r}>
                      <code>{r}</code>
                    </li>
                  ))}
              </ul>
            ) : null}
            <AssetRefList pid={pid} refs={collectAssetRefs(m.attachments, m.body)} />
          </li>
        ))}
      </ol>
      <p className="small muted">{t("mail.readonly_hint")}</p>
    </article>
  );
}

/** 人给 agent 发邮件（新线程） */
function ComposeDialog({ pid, open, onClose, agents, defaultTo }: { pid: string; open: boolean; onClose(): void; agents: Agent[]; defaultTo?: string }) {
  const { t } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { api } = useServices();
  const qc = useQueryClient();
  const [to, setTo] = useState<string[] | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState<MailPriority>("normal");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recipients = to ?? (defaultTo ? [defaultTo] : []);
  const ok = recipients.length > 0 && subject.trim() && body.trim();
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await api.call("mailSend", { params: { pid }, body: { to: recipients as AgentId[], subject: subject.trim(), body, priority } });
      await qc.invalidateQueries({ queryKey: ck.mailboxAll });
      toast.show({ message: t("mail.sent"), tone: "success", duration: 2000 });
      setSubject("");
      setBody("");
      setTo(null);
      setPriority("normal");
      onClose();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("mail.compose")}
      closeLabel={t("shell.close")}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" icon="send" loading={busy} disabled={!ok} onClick={() => void submit()} data-testid="mail-send">
            {t("mail.send")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit}>
        <fieldset className="tpl-scopes">
          <legend className="ui-field__label">{t("mail.recipients")}</legend>
          <div className="tpl-scopes__grid">
            {agents.map((a) => (
              <label key={a.id} className="check">
                <input
                  type="checkbox"
                  checked={recipients.includes(a.id)}
                  onChange={(e) => setTo(e.target.checked ? [...recipients, a.id] : recipients.filter((x) => x !== a.id))}
                  data-testid={`mail-to-${a.name}`}
                />
                <span>{a.name}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <Field label={t("mail.subject")} error={error ?? undefined}>
          {(id) => <Input id={id} value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} data-testid="mail-subject" />}
        </Field>
        <Field label={t("mail.body")}>{(id) => <TextArea id={id} rows={6} value={body} onChange={(e) => setBody(e.target.value)} data-testid="mail-body" />}</Field>
        <Field label={t("mail.priority")}>
          {(id) => (
            <Select id={id} value={priority} onChange={(e) => setPriority(e.target.value as MailPriority)}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`mail.priority.${p}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </form>
    </Dialog>
  );
}
